import { Star } from 'lucide-react';
import { useState } from 'react';

const ShinyStar = ({ sizeClass, fill = 'gradient', testId }) => {
  // fill: 'gradient' = shiny bronze, 'empty' = stone-200, 'half' = gradient masked left
  if (fill === 'empty') {
    return <Star className={`${sizeClass} fill-stone-200 text-stone-200`} data-testid={testId} />;
  }
  return (
    <Star
      className={`${sizeClass} text-[#c97f59] shiny-star-fill`}
      style={{
        fill: 'url(#shinyBronzeGrad)',
        stroke: '#c97f59',
      }}
      data-testid={testId}
    />
  );
};

export const StarRating = ({ rating, maxRating = 5, size = 'md', showValue = true, interactive = false, onRate = null }) => {
  const [hoverRating, setHoverRating] = useState(0);
  const displayRating = hoverRating || rating;

  const sizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
    xl: 'w-6 h-6'
  };

  const handleClick = (starIndex, isLeftHalf) => {
    if (!interactive || !onRate) return;
    const value = isLeftHalf ? starIndex + 0.5 : starIndex + 1;
    onRate(value);
  };

  const handleHover = (starIndex, isLeftHalf) => {
    if (!interactive) return;
    setHoverRating(isLeftHalf ? starIndex + 0.5 : starIndex + 1);
  };

  return (
    <div className="flex items-center gap-1" data-testid="star-rating">
      {/* Shared SVG defs for shiny bronze gradient */}
      <svg width="0" height="0" className="absolute" aria-hidden="true">
        <defs>
          <linearGradient id="shinyBronzeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#d4956a" />
            <stop offset="25%" stopColor="#c97f59" />
            <stop offset="50%" stopColor="#e8b08a" />
            <stop offset="75%" stopColor="#c97f59" />
            <stop offset="100%" stopColor="#8a5535" />
          </linearGradient>
        </defs>
      </svg>
      <div className="flex items-center">
        {[...Array(maxRating)].map((_, index) => {
          const filled = displayRating >= index + 1;
          const halfFilled = !filled && displayRating >= index + 0.5;

          return (
            <div
              key={index}
              className={`relative ${sizeClasses[size]} ${interactive ? 'cursor-pointer' : 'cursor-default'}`}
              onMouseLeave={() => interactive && setHoverRating(0)}
              data-testid={`star-${index + 1}`}
            >
              {/* Background empty star */}
              <div className="absolute inset-0">
                {filled ? (
                  <ShinyStar sizeClass={sizeClasses[size]} fill="gradient" />
                ) : (
                  <ShinyStar sizeClass={sizeClasses[size]} fill="empty" />
                )}
              </div>

              {/* Half star overlay */}
              {halfFilled && (
                <div className="absolute inset-0 overflow-hidden" style={{ width: '50%' }}>
                  <ShinyStar sizeClass={sizeClasses[size]} fill="gradient" />
                </div>
              )}

              {/* Interactive click areas */}
              {interactive && (
                <>
                  <div
                    className="absolute inset-0 z-10"
                    style={{ width: '50%' }}
                    onClick={() => handleClick(index, true)}
                    onMouseEnter={() => handleHover(index, true)}
                    data-testid={`star-${index + 1}-left`}
                  />
                  <div
                    className="absolute inset-0 z-10"
                    style={{ left: '50%', width: '50%' }}
                    onClick={() => handleClick(index, false)}
                    onMouseEnter={() => handleHover(index, false)}
                    data-testid={`star-${index + 1}-right`}
                  />
                </>
              )}
            </div>
          );
        })}
      </div>
      {showValue && (
        <span className="ml-1 text-sm font-medium text-muted-foreground">
          {rating.toFixed(1)}
        </span>
      )}
    </div>
  );
};

export default StarRating;
